import { useEffect, useState } from 'react'
import {
  Home,
  ClipboardList,
  AlertTriangle,
  ChevronRight,
  ScrollText,
  Settings,
  BookOpen,
  ArrowLeft,
  MapPin,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation, useNavigate } from 'react-router'
import { cn } from '@/shared/lib/utils'
import { buttonVariants } from '@/components/ui/button'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

type NavLeaf = {
  kind: 'leaf'
  path: string
  labelKey: string
  icon: React.ReactNode
}

type NavGroup = {
  kind: 'group'
  id: string
  labelKey: string
  pcmParentLabelKey: string
  icon: React.ReactNode
  defaultCollapsed?: boolean
  children: Array<{
    path: string
    labelKey: string
    pcmChildLabelKey: string
  }>
}

const NAV_ENTRIES: Array<NavLeaf | NavGroup> = [
  {
    kind: 'leaf',
    path: '',
    labelKey: 'nav.introduction',
    icon: <Home size={18} />,
  },
  {
    kind: 'leaf',
    path: 'standards',
    labelKey: 'nav.standards',
    icon: <ClipboardList size={18} />,
  },
  {
    kind: 'leaf',
    path: 'machine-locations',
    labelKey: 'nav.machineLocations',
    icon: <MapPin size={18} />,
  },
  {
    kind: 'group',
    id: 'risk-assessment-section',
    labelKey: 'nav.riskAssessmentGroup.sidebar',
    pcmParentLabelKey: 'nav.riskAssessmentGroup.sidebar',
    icon: <AlertTriangle size={18} />,
    children: [
      {
        path: 'hazards',
        labelKey: 'nav.riskAssessmentGroup.hazards',
        pcmChildLabelKey: 'nav.riskAssessmentGroup.hazards',
      },
      {
        path: 'risk-reduction-measures',
        labelKey: 'nav.riskAssessmentGroup.riskReductionMeasures',
        pcmChildLabelKey: 'nav.riskAssessmentGroup.riskReductionMeasures',
      },
      {
        path: 'safety-functions',
        labelKey: 'nav.riskAssessmentGroup.safetyFunctions',
        pcmChildLabelKey: 'nav.riskAssessmentGroup.safetyFunctions',
      },
      {
        path: 'ehsrs-overview',
        labelKey: 'nav.riskAssessmentGroup.ehsrsOverview',
        pcmChildLabelKey: 'nav.riskAssessmentGroup.ehsrsOverview',
      },
      {
        path: 'risk-assessment',
        labelKey: 'nav.riskAssessmentGroup.assessmentDocument',
        pcmChildLabelKey: 'nav.riskAssessmentGroup.assessmentDocument',
      },
    ],
  },
  {
    kind: 'group',
    id: 'manual',
    labelKey: 'nav.manual.sidebar',
    pcmParentLabelKey: 'nav.assemblyInstructions.sidebar',
    icon: <BookOpen size={18} />,
    children: [
      {
        path: 'manual-generation',
        labelKey: 'nav.manual.generation',
        pcmChildLabelKey: 'nav.assemblyInstructions.generation',
      },
      {
        path: 'manual',
        labelKey: 'nav.manual.validation',
        pcmChildLabelKey: 'nav.assemblyInstructions.validation',
      },
    ],
  },
  {
    kind: 'leaf',
    path: 'declaration-of-conformity',
    labelKey: 'nav.declarationOfConformity',
    icon: <ScrollText size={18} />,
  },
  {
    kind: 'group',
    id: 'settings',
    labelKey: 'nav.settingsGroup.sidebar',
    pcmParentLabelKey: 'nav.settingsGroup.sidebar',
    icon: <Settings size={18} />,
    defaultCollapsed: true,
    children: [
      {
        path: 'settings/associated-parties',
        labelKey: 'nav.settingsGroup.associatedParties',
        pcmChildLabelKey: 'nav.settingsGroup.associatedParties',
      },
      {
        path: 'settings/product-metadata',
        labelKey: 'nav.settingsGroup.productMetadata',
        pcmChildLabelKey: 'nav.settingsGroup.productMetadata',
      },
      {
        path: 'settings/e-plan',
        labelKey: 'nav.settingsGroup.ePlan',
        pcmChildLabelKey: 'nav.settingsGroup.ePlan',
      },
    ],
  },
]

type ResolvedNavItem =
  | {
      kind: 'leaf'
      path: string
      href: string
      title: string
      mobileTitle: string
      icon: React.ReactNode
      isLocked: boolean
      isActive: boolean
    }
  | {
      kind: 'group'
      id: string
      parentTitle: string
      icon: React.ReactNode
      defaultCollapsed: boolean
      children: Array<{
        path: string
        href: string
        title: string
        mobileTitle: string
        isLocked: boolean
        isActive: boolean
      }>
    }

export interface ComplianceStepsNavProps extends React.HTMLAttributes<HTMLElement> {
  studioBaseHref: string
  backHref: string
  /** Compliance scenario display title; falls back to translated default when missing */
  directiveTitle?: string | null
  isStandardApplicabilityDone?: boolean
  /** When true, only Introduction, Standards, and Settings are enabled; other steps are locked */
  restrictToIntroAndStandards?: boolean
  /** When true, use assembly-instructions parent/child labels instead of Manual */
  isPartlyCompletedMachine?: boolean | null
}

function buildHref(studioBaseHref: string, path: string): string {
  return `${studioBaseHref}${path ? `/${path}` : ''}`
}

function isSettingsChildPath(path: string): boolean {
  return path.startsWith('settings/')
}

/** Top-level nav row (standalone links and collapsible parents) — shared layout and ghost button base */
const COMPLIANCE_NAV_TOP_LEVEL_ROW = cn(
  buttonVariants({ variant: 'ghost' }),
  'inline-flex h-9 w-full items-center justify-start gap-2 px-3 font-normal lg:w-full'
)

/** Indented sub-links under collapsible groups — same typography and hover as top-level */
const COMPLIANCE_NAV_NESTED_ROW = cn(
  buttonVariants({ variant: 'ghost' }),
  'inline-flex h-9 w-full items-center justify-start rounded-md px-2 text-xs font-medium lg:w-full'
)

function resolveNav(
  translate: (key: string) => string,
  studioBaseHref: string,
  pathname: string,
  isStandardApplicabilityDone: boolean,
  restrictToIntroAndStandards: boolean,
  isPartlyCompletedMachine: boolean
): ResolvedNavItem[] {
  const resolveLeafLabelKey = (path: string, labelKey: string): string => {
    if (isPartlyCompletedMachine && path === 'declaration-of-conformity') {
      return 'nav.declarationOfIncorporation'
    }
    return labelKey
  }

  const result: ResolvedNavItem[] = []

  for (const entry of NAV_ENTRIES) {
    if (entry.kind === 'leaf') {
      const labelKey = resolveLeafLabelKey(entry.path, entry.labelKey)
      const onlyIntroAndStandardsAllowed =
        restrictToIntroAndStandards &&
        entry.path !== '' &&
        entry.path !== 'standards'
      const isLocked =
        onlyIntroAndStandardsAllowed ||
        (!isStandardApplicabilityDone && entry.path !== 'standards')
      const href = buildHref(studioBaseHref, entry.path)
      const isActive =
        href === pathname ||
        (entry.path !== '' && pathname.startsWith(`${href}/`))
      const title = translate(labelKey)
      result.push({
        kind: 'leaf',
        path: entry.path,
        href,
        title,
        mobileTitle: title,
        icon: entry.icon,
        isLocked,
        isActive,
      })
      continue
    }

    const parentTitle = isPartlyCompletedMachine
      ? translate(entry.pcmParentLabelKey)
      : translate(entry.labelKey)

    const children = entry.children.map((child) => {
      const childLabelKey = isPartlyCompletedMachine
        ? child.pcmChildLabelKey
        : child.labelKey
      const isSettingsChild = isSettingsChildPath(child.path)
      const onlyIntroAndStandardsAllowed =
        !isSettingsChild &&
        restrictToIntroAndStandards &&
        child.path !== '' &&
        child.path !== 'standards'
      const isLocked =
        onlyIntroAndStandardsAllowed ||
        (!isSettingsChild &&
          !isStandardApplicabilityDone &&
          child.path !== 'standards')
      const href = buildHref(studioBaseHref, child.path)
      const isActive =
        href === pathname ||
        (child.path !== '' && pathname.startsWith(`${href}/`))
      const title = translate(childLabelKey)
      const mobileTitle = `${parentTitle} — ${title}`
      return {
        path: child.path,
        href,
        title,
        mobileTitle,
        isLocked,
        isActive,
      }
    })

    result.push({
      kind: 'group',
      id: entry.id,
      parentTitle,
      icon: entry.icon,
      defaultCollapsed: entry.defaultCollapsed ?? false,
      children,
    })
  }

  return result
}

function ComplianceNavGroup({
  entry,
}: {
  entry: Extract<ResolvedNavItem, { kind: 'group' }>
}) {
  const hasActiveChild = entry.children.some((child) => child.isActive)
  const [open, setOpen] = useState(!entry.defaultCollapsed)

  useEffect(() => {
    if (hasActiveChild) {
      setOpen(true)
    }
  }, [hasActiveChild])

  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      className='group/collapsible flex w-full flex-col gap-0.5 lg:w-full'
    >
      <CollapsibleTrigger
        render={
          <button
            type='button'
            className={cn(
              COMPLIANCE_NAV_TOP_LEVEL_ROW,
              'text-muted-foreground hover:text-foreground'
            )}
          />
        }
      >
        <span className='shrink-0'>{entry.icon}</span>
        <span className='min-w-0 flex-1 truncate text-start text-xs font-medium'>
          {entry.parentTitle}
        </span>
        <ChevronRight
          className='ms-auto size-4 shrink-0 transition-transform duration-200 in-data-panel-open:rotate-90 rtl:rotate-180'
          aria-hidden
        />
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className='ms-3 flex flex-col gap-0.5 border-s border-border ps-2'>
          {entry.children.map((child) => {
            if (child.isLocked) {
              return (
                <div
                  key={child.path}
                  className={cn(
                    COMPLIANCE_NAV_NESTED_ROW,
                    'cursor-not-allowed text-muted-foreground'
                  )}
                >
                  <span className='min-w-0 flex-1 truncate text-start'>
                    {child.title}
                  </span>
                </div>
              )
            }
            return (
              <Link
                key={child.path}
                to={child.href}
                className={cn(
                  COMPLIANCE_NAV_NESTED_ROW,
                  child.isActive
                    ? 'bg-muted text-foreground hover:bg-accent'
                    : 'text-muted-foreground hover:text-foreground'
                )}
              >
                <span className='min-w-0 flex-1 truncate text-start'>
                  {child.title}
                </span>
              </Link>
            )
          })}
        </div>
      </CollapsibleContent>
    </Collapsible>
  )
}

export function ComplianceStepsNav({
  studioBaseHref,
  backHref,
  directiveTitle,
  isStandardApplicabilityDone = true,
  restrictToIntroAndStandards = false,
  isPartlyCompletedMachine = false,
  className,
  ...props
}: ComplianceStepsNavProps) {
  const { t } = useTranslation('machineryDirectiveCompliance')
  const resolvedDirectiveTitle =
    directiveTitle?.trim() || t('machineryDirective.title')
  const { pathname } = useLocation()
  const navigate = useNavigate()

  const resolved = resolveNav(
    t,
    studioBaseHref,
    pathname,
    isStandardApplicabilityDone,
    restrictToIntroAndStandards,
    Boolean(isPartlyCompletedMachine)
  )

  const flatForSelect: Array<{
    href: string
    mobileTitle: string
    icon: React.ReactNode
    isLocked: boolean
  }> = []
  for (const entry of resolved) {
    if (entry.kind === 'leaf') {
      flatForSelect.push({
        href: entry.href,
        mobileTitle: entry.mobileTitle,
        icon: entry.icon,
        isLocked: entry.isLocked,
      })
    } else {
      for (const child of entry.children) {
        flatForSelect.push({
          href: child.href,
          mobileTitle: child.mobileTitle,
          icon: entry.icon,
          isLocked: child.isLocked,
        })
      }
    }
  }

  const enabledItems = flatForSelect.filter((item) => !item.isLocked)
  const baseHref = buildHref(studioBaseHref, '')
  const selectValue = pathname
    ? (() => {
        const exact = enabledItems.find((item) => item.href === pathname)
        if (exact) {
          return exact.href
        }
        const parent = enabledItems.find((item) =>
          pathname.startsWith(`${item.href}/`)
        )
        return parent ? parent.href : baseHref
      })()
    : baseHref

  const [val, setVal] = useState(selectValue)
  useEffect(() => {
    setVal(selectValue)
  }, [selectValue])

  const handleSelect = (path: string | null) => {
    if (!path) {
      return
    }
    setVal(path)
    navigate(path)
  }

  return (
    <div className='flex flex-col gap-2'>
      <div className='px-1 pt-1 md:px-1 md:pt-0'>
        <Link
          to={backHref}
          className={cn(
            COMPLIANCE_NAV_TOP_LEVEL_ROW,
            'text-muted-foreground hover:text-foreground'
          )}
        >
          <ArrowLeft className='size-4 shrink-0 rtl:rotate-180' aria-hidden />
          <span className='min-w-0 flex-1 truncate text-start text-xs font-medium'>
            {resolvedDirectiveTitle}
          </span>
        </Link>
      </div>

      <div className='md:hidden'>
        <Select
          value={val}
          onValueChange={(value) => {
            if (value) {
              handleSelect(value)
            }
          }}
          items={enabledItems.map((item) => ({
            value: item.href ?? '',
            label: item.mobileTitle,
          }))}
        >
          <SelectTrigger className='w-full'>
            <SelectValue placeholder={t('nav.introduction')} />
          </SelectTrigger>
          <SelectContent>
            {enabledItems.map((item) => (
              <SelectItem key={item.href} value={item.href}>
                <div className='flex gap-x-4 px-2 py-1'>
                  <span className='scale-125'>{item.icon}</span>
                  <span className='text-md'>{item.mobileTitle}</span>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <ScrollArea
        orientation='horizontal'
        type='always'
        className={cn(
          'hidden w-full',
          isPartlyCompletedMachine ? 'min-w-72' : 'min-w-40',
          'bg-background px-1 py-2 md:block'
        )}
      >
        <nav
          className={cn(
            'flex space-x-2 py-1 lg:flex-col lg:space-y-1 lg:space-x-0',
            className
          )}
          {...props}
        >
          {resolved.map((entry) => {
            if (entry.kind === 'leaf') {
              if (entry.isLocked) {
                return (
                  <div
                    key={entry.path || 'intro'}
                    className={cn(
                      COMPLIANCE_NAV_TOP_LEVEL_ROW,
                      'cursor-not-allowed text-muted-foreground'
                    )}
                  >
                    <span className='shrink-0'>{entry.icon}</span>
                    <span className='min-w-0 flex-1 truncate text-start text-xs font-medium'>
                      {entry.title}
                    </span>
                  </div>
                )
              }
              return (
                <Link
                  key={entry.path || 'intro'}
                  to={entry.href}
                  className={cn(
                    COMPLIANCE_NAV_TOP_LEVEL_ROW,
                    entry.isActive
                      ? 'bg-muted text-foreground hover:bg-accent'
                      : 'text-muted-foreground hover:text-foreground'
                  )}
                >
                  <span className='shrink-0'>{entry.icon}</span>
                  <span className='min-w-0 flex-1 truncate text-start text-xs font-medium'>
                    {entry.title}
                  </span>
                </Link>
              )
            }

            return <ComplianceNavGroup key={entry.id} entry={entry} />
          })}
        </nav>
      </ScrollArea>
    </div>
  )
}
